import * as React from 'react';

import { toTimeZone } from '@internationalized/date';
import { enumRouteSortOption } from '@rematter/frontend-lib-api-client';
import {
  Badge,
  Button,
  Content,
  DataGrid,
  DataGridColumn,
  FloatingToolbar,
  Footer,
  HeaderIcons,
  Loading,
  Pagination,
  SearchField,
  Separator,
  Toolbar,
  type PaginationProps,
  useDataGridSelection,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue } from 'jotai';
import { Trash } from 'lucide-react';

import { RouteDialog } from '../../components/load-board/RouteDialog';

import { useTranslations } from '@/transportation/i18n/useTranslations';
import {
  $limit,
  $offset,
  $searchTerm,
} from '~/modules/transportation/_state/routesList.state';
import { useDeleteRouteMutation } from '~/modules/transportation/hooks/mutations/useDeleteRouteMutation';
import { useRoutesQuery } from '~/modules/transportation/hooks/queries/useRoutesQuery';
import { TransportationRootLayout } from '~/modules/transportation/layouts/TransportationRootLayout';
import { RouteFormState, RouteRecord } from '~/modules/transportation/types';
import useDebouncedValue from '~/shared/hooks/useDebounce';
import { $localization } from '~/shared/state/preferences.state';

export const RoutesListView = () => {
  const toast = useToast();
  const t = useTranslations();
  const localization = useAtomValue($localization);

  const [selectedRows, setSelectedRows] = React.useState<RouteRecord[]>([]);
  const [searchTerm, setSearchTerm] = useAtom($searchTerm);
  const [offset, setOffset] = useAtom($offset);
  const [limit, setLimit] = useAtom($limit);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [editRoute, setEditRoute] = React.useState<
    (RouteFormState & { id: string }) | undefined
  >(undefined);

  const debouncedSearchTerm = useDebouncedValue(searchTerm ?? '', 500);

  const { data, error, isLoading, refetch } = useRoutesQuery({
    searchTerm: debouncedSearchTerm || undefined,
    limit,
    offset,
    sorting: [enumRouteSortOption.CreatedAtDesc],
  });

  const routes: RouteRecord[] = data?.routes ?? [];

  const { mutateAsync: deleteRoute } = useDeleteRouteMutation();

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value || undefined);
    setOffset(0);
  };

  const handleEdit = (route: RouteRecord) => {
    setEditRoute({
      id: route.id,
      description: route.description,
      status: route.status,
      routeDate: route.routeDate,
      startedAt: route.startedAt,
      completedAt: route.completedAt,
      driverId: route.driverId,
      facilityGroupId: route.facilityGroupId,
      serviceIds: route.services.map((service) => service.id),
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await Promise.all(selectedRows.map((row) => deleteRoute({ id: row.id })));
      toast.success(t.routesView.messages.deleteSuccess(selectedRows.length));
      setSelectedRows([]);
      refetch();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t.routesView.messages.error,
      );
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditRoute(undefined);
  };

  const handleDialogSuccess = () => {
    refetch();
    handleCloseDialog();
  };

  const handleSelectionChange = useDataGridSelection(routes, setSelectedRows);

  const COLUMNS: DataGridColumn<RouteRecord>[] = [
    {
      key: 'description',
      header: t.routesView.table.columns.name,
      icon: HeaderIcons.Text,
      width: 300,
      resizable: true,
      sortable: true,
      cell: ({ row }) => (
        <div>
          {row.description}
          {row.documentNumber && (
            <span className="ml-2 text-neutral/80">({row.documentNumber})</span>
          )}
        </div>
      ),
    },
    {
      key: 'routeDate',
      header: t.routesView.table.columns.routeDate,
      icon: HeaderIcons.Date,
      width: 150,
      resizable: true,
      sortable: true,
      cell: ({ row }) =>
        row.routeDate
          ? toTimeZone(row.routeDate, localization.timezone)
              .toDate()
              .toLocaleDateString()
          : '-',
    },
    {
      key: 'driver',
      header: t.routesView.table.columns.driver,
      icon: HeaderIcons.Company,
      width: 200,
      resizable: true,
      cell: ({ row }) =>
        row.driver ? `${row.driver.firstName} ${row.driver.lastName}` : '-',
    },
    {
      key: 'services',
      header: t.routesView.table.columns.services,
      icon: HeaderIcons.Text,
      width: 100,
      resizable: true,
      cell: ({ row }) => row.services.length,
    },
    {
      key: 'actions',
      header: '',
      width: 100,
      cell: ({ row }) => (
        <Button
          color="primary"
          className="border border-primary/40 shadow-sm shadow-neutral/10"
          onPress={() => handleEdit(row)}
          size="small"
        >
          {t.routesView.table.actions.edit}
        </Button>
      ),
    },
  ];

  if (error) {
    return (
      <TransportationRootLayout>
        <div className="p-4 text-failure">
          {t.routesView.content.error(error.message)}
        </div>
      </TransportationRootLayout>
    );
  }

  return (
    <TransportationRootLayout className="@RoutesListView">
      <Toolbar>
        <div className="flex items-center gap-4">
          <SearchField
            value={searchTerm ?? ''}
            onChange={handleSearch}
            placeholder={t.routesView.toolbar.search}
          />
        </div>
      </Toolbar>

      <FloatingToolbar visible={selectedRows.length > 0}>
        <span className="text-sm">
          <Badge color="primary" size="small">
            {selectedRows.length}
          </Badge>{' '}
          selected
        </span>
        <Separator hideDivider />
        <Button
          variant="solid"
          color="failure"
          icon={<Trash />}
          isDisabled={selectedRows.length < 1}
          onPress={handleDelete}
        >
          {t.routesView.toolbar.delete(selectedRows.length)}
        </Button>
      </FloatingToolbar>

      <Content>
        <Loading isLoading={isLoading}>
          <DataGrid<RouteRecord>
            columns={COLUMNS}
            dataSource={routes}
            rowKey="id"
            aria-label={t.routesView.table.aria}
            selectionMode="multiple"
            onSelectionChange={handleSelectionChange}
            className="w-full text-sm"
            fullWidth
          />
        </Loading>
      </Content>

      <Footer>
        <Pagination
          limit={limit}
          offset={offset}
          total={data?.total ?? 0}
          onChange={handlePaginationChange}
        />
      </Footer>

      <RouteDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        onSuccess={handleDialogSuccess}
        route={editRoute}
      />
    </TransportationRootLayout>
  );
};
